import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios instance for authenticated admin calls (sends cookies)
const adminAxios = axios.create({ baseURL: API, withCredentials: true });

export const api = {
  // Tours
  getTours: async (category) => {
    const params = category ? { category } : {};
    const response = await axios.get(`${API}/tours`, { params });
    return response.data;
  },
  
  getTour: async (tourId) => {
    const response = await axios.get(`${API}/tours/${tourId}`);
    return response.data;
  },
  
  // Bookings
  createBooking: async (bookingData) => {
    const response = await axios.post(`${API}/bookings`, bookingData);
    return response.data;
  },
  
  getBooking: async (bookingId) => {
    const response = await axios.get(`${API}/bookings/${bookingId}`);
    return response.data;
  },
  
  getBookings: async (status) => {
    const params = status ? { status } : {};
    const response = await axios.get(`${API}/bookings`, { params });
    return response.data;
  },
  
  updateBookingStatus: async (bookingId, status) => {
    const response = await axios.put(`${API}/bookings/${bookingId}/status?status=${status}`);
    return response.data;
  },
  
  // Payments
  createStripeCheckout: async (bookingId, paymentMethod) => {
    const response = await axios.post(`${API}/payments/stripe/checkout`, {
      booking_id: bookingId,
      payment_method: paymentMethod,
      origin_url: window.location.origin
    });
    return response.data;
  },
  
  getPaymentStatus: async (sessionId) => {
    const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
    return response.data;
  },
  
  // Reviews
  getReviews: async () => {
    const response = await axios.get(`${API}/reviews`);
    return response.data;
  },
  
  // Contact
  sendContactMessage: async (messageData) => {
    const response = await axios.post(`${API}/contact`, messageData);
    return response.data;
  },
  
  getContactMessages: async () => {
    const response = await axios.get(`${API}/contact`);
    return response.data;
  },
  
  // Admin Stats
  getAdminStats: async () => {
    const response = await axios.get(`${API}/admin/stats`);
    return response.data;
  },

  // Surveys
  getSurveyContext: async (bookingId, token) => {
    const response = await axios.get(`${API}/surveys/${bookingId}`, { params: { token } });
    return response.data;
  },

  submitSurvey: async (bookingId, payload) => {
    const response = await axios.post(`${API}/surveys/${bookingId}`, payload);
    return response.data;
  },

  // Auth (cookies)
  authMe: async () => {
    const response = await adminAxios.get('/auth/me');
    return response.data;
  },
  authLogout: async () => {
    const response = await adminAxios.post('/auth/logout');
    return response.data;
  },

  // Admin (cookie-authenticated)
  adminListCustomers: async (search = '') => {
    const response = await adminAxios.get('/admin/customers', { params: { search } });
    return response.data;
  },
  adminGetCustomer: async (email) => {
    const response = await adminAxios.get(`/admin/customers/${encodeURIComponent(email)}`);
    return response.data;
  },
  adminCreateCustomer: async (payload) => {
    const response = await adminAxios.post('/admin/customers', payload);
    return response.data;
  },
  adminDeleteManualCustomer: async (email) => {
    const response = await adminAxios.delete(`/admin/customers/${encodeURIComponent(email)}`);
    return response.data;
  },
  adminListBookings: async (params = {}) => {
    const response = await adminAxios.get('/admin/bookings-list', { params });
    return response.data;
  },
  adminListSurveys: async () => {
    const response = await adminAxios.get('/admin/surveys');
    return response.data;
  },
  adminUpdateSurveyFollowup: async (surveyId, payload) => {
    const response = await adminAxios.put(`/admin/surveys/${surveyId}/followup`, payload);
    return response.data;
  },
  adminDeleteSurvey: async (surveyId) => {
    const response = await adminAxios.delete(`/admin/surveys/${surveyId}`);
    return response.data;
  },
  adminSendSurveyCampaign: async (payload) => {
    const response = await adminAxios.post('/admin/send-survey-campaign', payload);
    return response.data;
  },
  adminSendSurveyIndividual: async (bookingId, payload = {}) => {
    const response = await adminAxios.post(`/admin/send-survey/${bookingId}`, payload);
    return response.data;
  },
  adminSendSurveyManual: async (payload) => {
    const response = await adminAxios.post('/admin/send-survey-manual', payload);
    return response.data;
  },
  adminListSurveyInvites: async () => {
    const response = await adminAxios.get('/admin/survey-invites');
    return response.data;
  },
  adminGetPaidCustomersCount: async () => {
    const response = await adminAxios.get('/admin/paid-customers-count');
    return response.data;
  },
  adminExportSurveysPdf: async () => {
    const response = await adminAxios.get('/admin/surveys/export-pdf', { responseType: 'blob' });
    return response.data;
  },
  adminListReviews: async () => {
    const response = await adminAxios.get('/admin/reviews');
    return response.data;
  },
  adminCreateReview: async (payload) => {
    const response = await adminAxios.post('/admin/reviews', payload);
    return response.data;
  },
  adminUpdateReview: async (id, payload) => {
    const response = await adminAxios.put(`/admin/reviews/${id}`, payload);
    return response.data;
  },
  adminDeleteReview: async (id) => {
    const response = await adminAxios.delete(`/admin/reviews/${id}`);
    return response.data;
  },
  adminUploadReviewPhoto: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const response = await adminAxios.post('/admin/reviews/upload-photo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  adminRefreshVoiceAgent: async () => {
    const response = await adminAxios.post('/admin/voice-agent/refresh-knowledge');
    return response.data;
  },
  adminGetStats: async () => {
    const response = await adminAxios.get('/admin/stats');
    return response.data;
  },
  adminListTours: async () => {
    const response = await adminAxios.get('/admin/tours-list');
    return response.data;
  },
  adminPatchTour: async (tourId, patch) => {
    const response = await adminAxios.patch(`/admin/tours/${tourId}`, patch);
    return response.data;
  }
};
